import { createFileRoute } from "@tanstack/react-router";
import { LocalServicePage } from "@/components/LocalServicePage";
import __apexTheater from "@/assets/Apex_Theater.webp.asset.json";
const hero = __apexTheater.url;
import apexTheaterRichImg from "@/assets/Apex_Theater.webp.asset.json";
import { localBusinessSchema, NEIGHBORHOOD_GEO } from "@/lib/localBusinessSchema";

const NEIGHBORHOOD = "Castle Pines";
const SLUG = "castle-pines";
const ZIP = "80108";
const URL = "https://apexav.com/castle-pines/home-theater-installation";

const faqs = [
  {
    q: "How much does a Castle Pines home theater installation cost?",
    a: "Castle Pines projects typically start around $40,000 for a finished basement media room with 4K laser projection and 7.2.4 Dolby Atmos. The Castle Pines Village gated estates we work in most often run $100,000–$400,000+ with full reference-grade engineering, tiered seating, fiber-optic starfields, and integrated lighting and shading. Every project is fixed-bid before construction begins.",
  },
  {
    q: "Do you work inside Castle Pines Village (the gated community)?",
    a: "Yes — Castle Pines Village is one of our core service areas. We're familiar with the HOA architectural review process and submit drawings and equipment specs on your behalf. The estates inside the gates have the ceiling height, square footage, and structural mass for true reference theaters with full acoustic engineering.",
  },
  {
    q: "What's the difference between a media room and a dedicated theater?",
    a: "A media room is multi-purpose — open to the rest of the lower level, ambient light tolerated, typically 5.1.4 or 7.2.4 Atmos with a large flat panel or short-throw projector. A dedicated theater is a sealed, light-controlled, acoustically engineered room built for one purpose: reference-grade movie playback. Most Castle Pines homes we touch end up with both.",
  },
  {
    q: "Will the theater integrate with my existing smart home system?",
    a: "Yes. The theater becomes another zone on your existing platform — same remote, same app, same scenes. Lights, shades, projector, screen, and source switching all respond to one button.",
  },
  {
    q: "How long does a Castle Pines theater install take?",
    a: "Plan on 10–16 weeks for a dedicated reference-grade room: 3 weeks of design and engineering, 5–10 weeks for construction, and 2–3 weeks for AV install, calibration, and programming. Media-room installs without construction wrap in 3–5 weeks.",
  },
];

const testimonials = [
  { quote: "Apex built us a true reference theater in our Castle Pines Village estate — 9.4.6 Atmos, dual 4K laser, tiered seating, the works. Picture and sound are simply unreal.", name: "Robert K.", location: "Castle Pines Village" },
  { quote: "They handled the HOA submission, coordinated with our designer, and delivered exactly what was on the drawings. No surprises, no change orders.", name: "Jennifer M.", location: "Castle Pines Village" },
  { quote: "Our basement media room is now the most-used room in the house. The kids watch sports, we watch movies, and one button handles everything.", name: "Andrew T.", location: "Castle Pines" },
];

export const Route = createFileRoute("/castle-pines/home-theater-installation")({
  head: () => ({
    meta: [
      { title: "Home Theater Installation Castle Pines | Apex AV Denver" },
      { name: "description", content: "Reference-grade home theater design & installation in Castle Pines Village. Dedicated theaters from $40K. Free in-home consultation." },
      { name: "keywords", content: "home theater installation Castle Pines, Castle Pines Village home theater, custom home theater Castle Pines, Dolby Atmos Castle Pines, dedicated theater Castle Pines Village, basement theater Castle Pines, 4K projector Castle Pines, reference theater Castle Pines" },
      { property: "og:title", content: "Home Theater Installation Castle Pines — Apex AV" },
      { property: "og:description", content: "Reference-grade home theater design & installation in Castle Pines & Castle Pines Village." },
      { property: "og:image", content: `https://apexav.com${apexTheaterRichImg.url}` },
      { property: "og:image:alt", content: "Apex AV custom home theater installation in Denver, Colorado" },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:image", content: `https://apexav.com${apexTheaterRichImg.url}` },
      { name: "twitter:title", content: "Home Theater Installation Castle Pines — Apex AV" },
      { name: "twitter:description", content: "Reference-grade home theater design & installation in Castle Pines." },
    ],
    links: [{ rel: "canonical", href: URL }, { rel: "preload", as: "image", href: hero, fetchPriority: "high" } as never],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Service",
              name: `Home Theater Installation in ${NEIGHBORHOOD}`,
              description: `Reference-grade home theater design and installation in ${NEIGHBORHOOD}, CO.`,
              url: URL,
              provider: { "@id": "https://apexav.com/#organization" },
              areaServed: { "@type": "Place", name: `${NEIGHBORHOOD}, CO ${ZIP}` },
              serviceType: "Custom Home Theater Installation",
              offers: { "@type": "Offer", priceCurrency: "USD", price: "40000" },
            },
            localBusinessSchema({
              neighborhood: NEIGHBORHOOD,
              url: URL,
              image: `https://apexav.com${apexTheaterRichImg.url}`,
              zip: ZIP,
              latitude: NEIGHBORHOOD_GEO[SLUG].latitude,
              longitude: NEIGHBORHOOD_GEO[SLUG].longitude,
              priceFloor: 30000,
              serviceName: "Home Theater Installation",
            }),
            {
              "@type": "FAQPage",
              mainEntity: faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ],
        }),
      },
    ],
  }),
  component: () => (
    <LocalServicePage
      neighborhood={NEIGHBORHOOD}
      neighborhoodSlug={SLUG}
      zip={ZIP}
      service="home theater installation"
      serviceKey="home-theater"
      parentServiceLabel="home theater"
      parentServiceTo="/services/home-theater"
      h1="Home Theater Installation in Castle Pines, CO"
      intro="Reference-grade dedicated theaters and media rooms designed for Castle Pines and Castle Pines Village estates. Full acoustic engineering, Dolby Atmos surround, 4K and 8K laser projection, motorized screens, tiered seating, and integration with your existing smart home system."
      hero={hero}
      bullets={[
        "Castle Pines Village HOA submission included",
        "9.4.6 Dolby Atmos design & calibration",
        "4K and 8K laser projection (JVC, Sony, Barco)",
        "Full acoustic engineering for reference SPL",
        "Hidden in-wall speakers, racks in mechanical closets",
        "Free in-home design consultation",
      ]}
      microNeighborhoods={["Castle Pines Village", "The Village at Castle Pines", "Surrey Ridge", "Hidden Pointe", "Esperanza", "Glen Oaks"]}
      testimonials={testimonials}
      faqs={faqs}
      bodyParagraphs={[
        {
          heading: "Built for Castle Pines Village estates",
          body: (
            <>
              <p>
                The gated estates in Castle Pines Village have the ceiling height, square footage, structural mass, and budget for true reference theaters — but they also have HOA architectural review, custom finishes, and architects and interior designers we have to coordinate with. We've done dozens of theaters inside the gates and we know the playbook: HOA submission, designer coordination, fixed-scope drawings, and a finished room that disappears into the architecture.
              </p>
              <p>
                Every Castle Pines project starts with a free in-home walkthrough. We measure the room, listen to how you actually watch (movies? concerts? sports? all of the above?), review your existing control system, and meet your designer if needed. From there we deliver a fixed-scope design before any construction begins.
              </p>
            </>
          ),
        },
        {
          heading: "What a Castle Pines reference theater includes",
          body: (
            <>
              <p>
                A reference-grade Castle Pines theater is engineered end-to-end: <strong>9.4.6 Dolby Atmos surround</strong> with reference-class amplification, <strong>4K or 8K laser projection</strong> from JVC, Sony, or Barco, motorized screens from Stewart or Screen Innovations, full <strong>acoustic engineering</strong> (decoupled walls, RSIC clips, double drywall, isolated HVAC ducting, acoustically transparent fabric walls), tiered theater seating with bass shakers, fiber-optic starfield ceilings, and one-button scenes that dim the lights, drop the shades, and fire the projector.
              </p>
              <p>
                Equipment racks live in mechanical closets — never in the room. Speakers are in-wall behind acoustically transparent fabric. The only thing you see is the screen and the seats.
              </p>
            </>
          ),
        },
        {
          heading: "Integrated with the rest of your Castle Pines smart home",
          body: (
            <>
              <p>
                Almost every Castle Pines Village home we touch has an existing smart home system. The theater becomes another zone on the same platform — same remote, same app, same scenes. If you're planning broader smart home work as part of the same project, see our <a href="/castle-pines/smart-home-installation" className="text-brand-text underline underline-offset-2">Castle Pines smart home installation</a> page.
              </p>
            </>
          ),
        },
      ]}
    />
  ),
});
